import React, { useState, useRef, useEffect } from 'react';
import { 
    Box, 
    Container, 
    useTheme, 
    Typography, 
    Button, 
    Link, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions 
} from '@mui/material';
import HeroSlideshow from '../components/HeroSlideshow';
import UserConsole from '../components/UserConsole';
import CodeOwnerConsole from '../components/CodeOwnerConsole';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getCurrentToS, getCurrentPrivacyPolicy } from '../services/api';

function HomePage() {
    const [splitPosition, setSplitPosition] = useState(50);
    const [isDragging, setIsDragging] = useState(false);
    const splitDividerRef = useRef(null);
    const containerRef = useRef(null);
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogContent, setDialogContent] = useState('');
    const [dialogTitle, setDialogTitle] = useState('');

    const handleMouseDown = (e) => {
        setIsDragging(true);
        e.preventDefault();
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging || !containerRef.current) return;
            
            const containerRect = containerRef.current.getBoundingClientRect();
            const newPosition = ((e.clientX - containerRect.left) / containerRect.width) * 100;
            const limitedPosition = Math.min(Math.max(newPosition, 28), 72);
            setSplitPosition(limitedPosition);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    const handleViewToS = async () => {
        try {
            const { content } = await getCurrentToS();
            setDialogTitle('Terms of Service');
            setDialogContent(content
                .replace(/##/g, '\n\n')
                .replace(/\*\*/g, '')
                .replace(/\[|\]/g, '')
            );
            setOpenDialog(true);
        } catch (error) {
            console.error('Failed to load Terms of Service:', error);
        }
    };

    const handleViewPrivacyPolicy = async () => {
        try {
            const { content } = await getCurrentPrivacyPolicy();
            setDialogTitle('Privacy Policy');
            setDialogContent(content
                .replace(/##/g, '\n\n')
                .replace(/\*\*/g, '')
                .replace(/\[|\]/g, '')
            );
            setOpenDialog(true);
        } catch (error) {
            console.error('Failed to load Privacy Policy:', error);
        }
    };

    const handleContactClick = () => {
        const email = 'support@mytowncode.com';
        const subject = 'MyTownCode Support Request';
        const mailtoUrl = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}`;
        const link = document.createElement('a');
        link.href = mailtoUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            backgroundColor: theme.palette.background.default,
            transition: 'background-color 0.3s ease'
        }}>
            {/* Hero Header Section */}
            <Box sx={{ 
                pt: { xs: 9, sm: 11, md: 12 },
                pb: { xs: 1, sm: 1.5 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                px: { xs: 2, sm: 3 }
            }}>
                {/* Main Headline */}
                <Typography 
                    variant="h1" 
                    sx={{ 
                        color: theme.palette.text.primary,
                        fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.1rem' },
                        fontWeight: 800,
                        letterSpacing: '-0.03em',
                        lineHeight: 1.15,
                        maxWidth: '900px',
                        mx: 'auto',
                        mb: { xs: 1.5, sm: 2 }
                    }}
                >
                    Connect with Products and Services You Need
                </Typography>

                {/* Slideshow Display */}
                <HeroSlideshow />
            </Box>

            {/* Split Workspace Section (User Console & Code Owner Console) */}
            <Box sx={{ 
                flex: 1,
                width: '100%',
                py: { xs: 2, sm: 3, md: 4 },
                px: { xs: 1.5, sm: 2.5, md: 4 }
            }}>
                <Box 
                    ref={containerRef}
                    sx={{ 
                        maxWidth: '1400px',
                        mx: 'auto',
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        minHeight: { xs: 'auto', md: '640px' },
                        position: 'relative',
                        cursor: isDragging ? 'col-resize' : 'default',
                        gap: { xs: 2.5, md: 0 },
                    }}
                >
                    {/* Left Panel - User Console */}
                    <Box 
                        sx={{ 
                            width: { xs: '100%', md: `${splitPosition}%` },
                            p: { xs: 2, sm: 3 },
                            position: 'relative',
                            backgroundColor: theme.palette.background.paper,
                            boxShadow: isDark 
                                ? '0 10px 30px rgba(0, 0, 0, 0.4)' 
                                : '0 8px 30px rgba(15, 23, 42, 0.05)',
                            borderRadius: { xs: '18px', md: '22px' },
                            border: `1px solid ${theme.palette.divider}`,
                            display: 'flex',
                            flexDirection: 'column',
                            boxSizing: 'border-box'
                        }}
                    >
                        <UserConsole />
                    </Box>

                    {/* Draggable Divider (Desktop Only) */}
                    <Box
                        ref={splitDividerRef}
                        onMouseDown={handleMouseDown}
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            position: 'absolute',
                            left: `${splitPosition}%`,
                            top: 0,
                            bottom: 0,
                            width: '32px',
                            transform: 'translateX(-50%)',
                            cursor: 'col-resize',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10,
                            userSelect: 'none',
                            '&:hover .divider-pill': {
                                transform: 'scale(1.12)',
                                borderColor: theme.palette.primary.main,
                                boxShadow: isDark ? '0 8px 24px rgba(0, 102, 255, 0.4)' : '0 8px 24px rgba(0, 102, 255, 0.25)',
                            }
                        }}
                    >
                        <Box 
                            className="divider-pill"
                            sx={{
                                width: '28px',
                                height: '52px',
                                borderRadius: '14px',
                                backgroundColor: isDark ? '#1E293B' : '#FFFFFF',
                                border: `1.5px solid ${theme.palette.divider}`,
                                boxShadow: isDark 
                                    ? '0 8px 24px rgba(0, 0, 0, 0.6)' 
                                    : '0 4px 16px rgba(15, 23, 42, 0.12)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                        >
                            <ChevronLeftIcon sx={{ fontSize: '15px', color: theme.palette.text.secondary }} />
                            <ChevronRightIcon sx={{ fontSize: '15px', color: theme.palette.text.secondary, ml: '-6px' }} />
                        </Box>
                    </Box>

                    {/* Right Panel - Code Owner Console */}
                    <Box 
                        sx={{ 
                            width: { xs: '100%', md: `${100 - splitPosition}%` },
                            p: { xs: 2, sm: 3 },
                            overflow: 'auto',
                            position: 'relative',
                            backgroundColor: isDark ? '#111A2B' : '#F8FAFC',
                            boxShadow: isDark 
                                ? '0 10px 30px rgba(0, 0, 0, 0.4)' 
                                : '0 8px 30px rgba(15, 23, 42, 0.05)',
                            borderRadius: { xs: '18px', md: '22px' },
                            border: `1px solid ${theme.palette.divider}`,
                            display: 'flex',
                            flexDirection: 'column',
                            boxSizing: 'border-box'
                        }}
                    >
                        <CodeOwnerConsole />
                    </Box>
                </Box>
            </Box>

            {/* Footer */}
            <Box 
                sx={{ 
                    width: '100%',
                    backgroundColor: theme.palette.background.default,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    py: { xs: 3, sm: 4 },
                    mt: 'auto'
                }}
            >
                <Container maxWidth="lg">
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2
                    }}>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                color: theme.palette.text.secondary,
                                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                fontWeight: 500
                            }}
                        >
                            © 2024–2026 MyTownCode. All rights reserved.
                        </Typography>
                        <Box sx={{ 
                            display: 'flex', 
                            gap: { xs: 2, sm: 3 },
                            flexWrap: 'wrap',
                            alignItems: 'center'
                        }}>
                            <Link
                                component="button"
                                onClick={handleViewToS}
                                sx={{ 
                                    color: theme.palette.text.secondary,
                                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                    fontWeight: 500,
                                    textDecoration: 'none',
                                    transition: 'color 0.2s ease',
                                    '&:hover': {
                                        color: theme.palette.primary.main,
                                    }
                                }}
                            >
                                Terms of Service
                            </Link>
                            <Link
                                component="button"
                                onClick={handleViewPrivacyPolicy}
                                sx={{ 
                                    color: theme.palette.text.secondary,
                                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                    fontWeight: 500,
                                    textDecoration: 'none',
                                    transition: 'color 0.2s ease',
                                    '&:hover': {
                                        color: theme.palette.primary.main,
                                    }
                                }}
                            >
                                Privacy Policy
                            </Link>
                            <Typography 
                                variant="body2" 
                                onClick={handleContactClick}
                                sx={{ 
                                    color: theme.palette.text.secondary,
                                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'color 0.2s ease',
                                    '&:hover': { 
                                        color: theme.palette.primary.main 
                                    }
                                }}
                            >
                                Contact Us
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Terms / Privacy Modal */}
            <Dialog 
                open={openDialog} 
                onClose={() => setOpenDialog(false)}
                maxWidth="md"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        backgroundColor: theme.palette.background.paper,
                        backgroundImage: 'none',
                    }
                }}
            >
                <DialogTitle sx={{ 
                    pb: 1,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: theme.palette.text.primary
                }}>
                    {dialogTitle}
                </DialogTitle>
                <DialogContent dividers sx={{ 
                    whiteSpace: 'pre-wrap',
                    fontSize: { xs: '0.875rem', sm: '0.95rem' },
                    lineHeight: 1.65,
                    color: theme.palette.text.secondary,
                    borderColor: theme.palette.divider
                }}>
                    {dialogContent}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        variant="contained"
                        onClick={() => setOpenDialog(false)}
                        sx={{ borderRadius: '10px', px: 3 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default HomePage;
